import React from 'react';
import InfoTip from '../InfoTip';
import ReportLineRow from './ReportLineRow';
import { SECTION_ORDER, SECTION_TITLE, TechReport, bugFixesText } from './types';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400 print:shadow-none print:ring-0';

// The report on screen: Highlights, then Completed, Ongoing and Planned as one table of Task, Description and Status,
// then Roadblocks. What is in it is read from Jira; only a remark and the roadblocks are written by hand.
export default function ReportBody({ report, onChanged, roadblocks, onRoadblocks, onSaveRoadblocks }: { report: TechReport; onChanged: () => void; roadblocks: string; onRoadblocks: (v: string) => void; onSaveRoadblocks: () => void }) {
  return (
    <>
      <section className={`${CARD} mb-5 p-5`} aria-label="Highlights">
        <h2 className="text-base font-semibold text-gray-900">Highlights</h2>
        {report.highlights.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">Nothing reached production in this period.</p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm text-gray-800">
            {report.highlights.map((h) => (
              <li key={h.project_name} className="break-words">
                <span className="font-semibold">{h.project_name}:</span> {h.features.join('; ')}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${CARD} mb-5 overflow-hidden`} aria-label="Tasks">
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[40rem] border-separate border-spacing-0 text-left text-sm">
            <caption className="sr-only">The report: task, description and status</caption>
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                <th scope="col" className="border-b border-gray-200 px-4 py-2">Task</th>
                <th scope="col" className="border-b border-gray-200 px-3 py-2">Description</th>
                <th scope="col" className="border-b border-gray-200 px-3 py-2">
                  <span className="inline-flex items-center">Status<InfoTip id="report.timeline" /></span>
                </th>
              </tr>
            </thead>
            {SECTION_ORDER.map((section) => {
              const lines = report.report[section];
              const bugs = section === 'completed' ? bugFixesText(report.bug_fixes) : null;
              return (
                <tbody key={section}>
                  <tr>
                    <th colSpan={3} scope="colgroup" className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      {SECTION_TITLE[section]} <span className="font-normal normal-case text-gray-600">· {lines.length}</span>
                    </th>
                  </tr>
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={3} className="border-b border-gray-100 px-4 py-3 text-gray-600">Nothing in this section.</td>
                    </tr>
                  )}
                  {lines.map((l) => (
                    <ReportLineRow key={`${section}:${l.key ?? l.feature}`} line={l} onChanged={onChanged} />
                  ))}
                  {bugs && (
                    <tr>
                      <td colSpan={3} className="border-b border-gray-100 px-4 py-3 italic text-gray-700">{bugs}</td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      </section>

      <section className={`${CARD} mb-5 p-5`} aria-label="Roadblocks">
        <label htmlFor="report-roadblocks" className="text-base font-semibold text-gray-900">Roadblocks and challenges</label>
        <p className="mt-1 text-xs text-gray-600 print:hidden">Written by hand. It saves when you leave the box.</p>
        <textarea
          id="report-roadblocks"
          rows={4}
          maxLength={2000}
          value={roadblocks}
          onChange={(e) => onRoadblocks(e.target.value)}
          onBlur={onSaveRoadblocks}
          className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 print:border-0 print:px-0"
        />
      </section>
    </>
  );
}
